/**
 * =============================================================================
 * SCRIPT-NAME:   PrognoseTracker.js
 * MODUL:         Ads Dashboard – Prognose-Tracker (Stufe-1 Beweis-Loop)
 * BESCHREIBUNG:  Zeigt die Prognosen (ads_prognosen) + laufende Trefferquote.
 *                Verdikte schreibt der Cron-Richter (gads_prognose_check.php),
 *                hier nur Anzeige (read-only).
 * VERSION:       v1.0  DATUM: 2026-06-30
 * =============================================================================
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const API = '/api/ads_prognosen.php';

const ST = {
  offen:     { label: 'offen',     icon: '⏳', color: '#6b7280', bg: '#f3f4f6' },
  getroffen: { label: 'getroffen', icon: '✅', color: '#16a34a', bg: '#ecfdf3' },
  verfehlt:  { label: 'verfehlt',  icon: '🔻', color: '#dc2626', bg: '#fef2f2' },
  teilweise: { label: 'teilweise', icon: '🟡', color: '#d97706', bg: '#fffbeb' },
};

function daysTo(d) {
  if (!d) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const t = new Date(d + 'T00:00:00');
  return Math.round((t - today) / 86400000);
}

export default function PrognoseTracker() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  const load = useCallback(() => {
    const token = localStorage.getItem('token') || '';
    setLoading(true); setError(null);
    const url = filter ? `${API}?limit=100&status=${filter}` : `${API}?limit=100`;
    fetch(url, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(d => {
        if (d && d.success) { setRows(d.data || []); setSummary(d.summary || null); }
        else setError(d.message || 'Fehler');
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const S = summary || { offen: 0, getroffen: 0, verfehlt: 0, teilweise: 0, bewertet: 0, trefferquote_pct: null, naechstes_pruef_datum: null };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 16px', fontFamily: 'system-ui, sans-serif', color: '#1f2937' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
        <Link to="/admin" style={{ textDecoration: 'none', color: '#2563eb', fontSize: 14 }}>← Admin</Link>
        <h1 style={{ fontSize: 22, margin: 0 }}>🎯 Prognose-Tracker</h1>
        <button onClick={load} title="Neu laden" style={{ marginLeft: 'auto', border: '1px solid #d1d5db', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>↻</button>
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', marginTop: 0 }}>
        Stufe-1-Beweis-Loop: jede GAds-Änderung mit falsifizierbarer Prognose. Verdikt am Prüf-Datum durch den deterministischen Richter (Cron). <b>Beweis vor Autonomie.</b>
      </p>

      {/* Trefferquote-Kachel */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '14px 0' }}>
        <div style={{ flex: '1 1 140px', background: '#111827', color: '#fff', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ fontSize: 12, opacity: 0.8 }}>Trefferquote</div>
          <div style={{ fontSize: 26, fontWeight: 700 }}>{S.trefferquote_pct === null ? '–' : S.trefferquote_pct + ' %'}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{S.bewertet} bewertet</div>
        </div>
        {['offen', 'getroffen', 'verfehlt', 'teilweise'].map(k => (
          <div key={k} style={{ flex: '1 1 90px', background: ST[k].bg, borderRadius: 10, padding: '12px 14px', border: '1px solid ' + ST[k].color + '33' }}>
            <div style={{ fontSize: 12, color: ST[k].color }}>{ST[k].icon} {ST[k].label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: ST[k].color }}>{S[k] ?? 0}</div>
          </div>
        ))}
      </div>
      {S.naechstes_pruef_datum && (
        <p style={{ fontSize: 13, color: '#374151', marginTop: -4 }}>
          Nächstes Prüf-Datum: <b>{S.naechstes_pruef_datum}</b>
          {daysTo(S.naechstes_pruef_datum) !== null && <span style={{ color: '#6b7280' }}> (in {daysTo(S.naechstes_pruef_datum)} Tagen)</span>}
        </p>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 14px' }}>
        {[['', 'alle'], ['offen', '⏳ offen'], ['getroffen', '✅ getroffen'], ['verfehlt', '🔻 verfehlt'], ['teilweise', '🟡 teilweise']].map(([v, l]) => (
          <button key={v || 'alle'} onClick={() => setFilter(v)}
            style={{ border: '1px solid ' + (filter === v ? '#2563eb' : '#d1d5db'), background: filter === v ? '#eff6ff' : '#fff', color: filter === v ? '#1d4ed8' : '#374151', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 13 }}>
            {l}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: '#6b7280' }}>Lädt …</p>}
      {error && <p style={{ color: '#dc2626' }}>⚠ {error}</p>}
      {!loading && !error && rows.length === 0 && <p style={{ color: '#6b7280' }}>Keine Prognosen für diesen Filter.</p>}

      {rows.map(p => {
        const st = ST[p.status] || ST.offen;
        const dt = daysTo(p.pruef_datum);
        const countdown = p.status === 'offen' && dt !== null ? (dt >= 0 ? `in ${dt} T` : `${-dt} T überfällig`) : '';
        return (
          <div key={p.id} style={{ border: '1px solid #e5e7eb', borderLeft: '4px solid ' + st.color, borderRadius: 8, padding: '12px 14px', marginBottom: 10, background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#6b7280' }}>
              <span style={{ fontWeight: 700 }}>#{p.id}</span>
              {p.lp_zuordnung && <span style={{ background: '#f3f4f6', borderRadius: 4, padding: '1px 6px' }}>{p.lp_zuordnung}</span>}
              <span style={{ background: '#eef2ff', color: '#4338ca', borderRadius: 4, padding: '1px 6px' }}>{p.akteur}</span>
              <span>Prüf: {p.pruef_datum} {countdown && <b style={{ color: '#374151' }}>· {countdown}</b>}</span>
              <span style={{ marginLeft: 'auto', color: st.color, fontWeight: 700 }}>{st.icon} {st.label}</span>
            </div>
            <div style={{ fontWeight: 600, margin: '6px 0 2px' }}>{p.aktion}</div>
            <div style={{ fontSize: 14 }}>{p.prognose}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Metrik: {p.metrik} · Erwartung: {p.erwartung}</div>
            {p.ergebnis && (
              <div style={{ marginTop: 6, fontSize: 13, background: st.bg, borderRadius: 6, padding: '6px 8px', color: st.color }}>
                <b>Verdikt ({p.bewertet_am ? p.bewertet_am.substring(0, 10) : ''}):</b> {p.ergebnis}{p.ergebnis_wert ? ` — ${p.ergebnis_wert}` : ''}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
